/**
 * @module cx
 * @package @geenius/adapters/react-css
 * @description Provides the lightweight class name composition helper used by
 * the React CSS variant. The helper keeps conditional CSS class assembly terse
 * without introducing Tailwind-specific dependencies.
 *
 * @param classes Class value strings, arrays, and object maps where falsy values are ignored
 * @returns A space-delimited class name string with duplicate tokens removed
 *
 * @example
 * ```ts
 * cx('button', 'button-primary') // 'button button-primary'
 * cx('button', isActive && 'button-active') // 'button button-active' or 'button'
 * cx('base', null, undefined, false, 'modifier') // 'base modifier'
 * cx('base', ['nested'], { 'is-active': true }) // 'base nested is-active'
 * ```
 */
export type ClassValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly ClassValue[]
  | { readonly [className: string]: unknown };

function addClassTokens(
  tokens: string[],
  seen: Set<string>,
  className: string,
): void {
  for (const token of className.trim().split(/\s+/)) {
    if (!token || seen.has(token)) {
      continue;
    }
    seen.add(token);
    tokens.push(token);
  }
}

function addClassValue(
  tokens: string[],
  seen: Set<string>,
  value: ClassValue,
): void {
  if (!value) {
    return;
  }
  if (typeof value === "string" || typeof value === "number") {
    addClassTokens(tokens, seen, String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      addClassValue(tokens, seen, item);
    }
    return;
  }
  if (typeof value === "object") {
    for (const [className, enabled] of Object.entries(value)) {
      if (enabled) {
        addClassTokens(tokens, seen, className);
      }
    }
  }
}

export function cx(...classes: ClassValue[]): string {
  const tokens: string[] = [];
  const seen = new Set<string>();
  for (const value of classes) {
    addClassValue(tokens, seen, value);
  }
  return tokens.join(" ");
}
